"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Badge } from "../../ui/badge"
import { Button } from "../../ui/button"
import { Plus, Edit, Circle } from 'lucide-react'
import { AddLeaveTypeModal } from "../../modals/add-leave-type-modal"
import { useToast } from "../../hooks/use-toast"
import axios from "axios"
import { Briefcase } from "lucide-react"

interface Policy {
  id: number
  name: string
  description: string
  status: string
  createdBy: string
  leaveTypes: string[]
}

type LeavePolicyRow = {
  policyID: number
  policyName: string
  leaveTypeID: number
  leaveName?: string
  description: string
  isActive: boolean
  createdBy: string
  createdOn?: string
  modifiedBy?: string
  modifiedOn?: string
}

type LeaveType = {
  leaveTypeId: number
  leaveName: string
  description?: string
  colorCode?: string
  isActive?: boolean
  createdBy?: string
  modifiedBy?: string
}

export function LeavePoliciesMergedPage() {
  const [policies, setPolicies] = useState<Policy[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingLeaveType, setEditingLeaveType] = useState<any | null>(null)
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const { toast } = useToast()

  const parseCodeFromName = (leaveName: string): string => {
    const match = leaveName.match(/\(([^)]+)\)/)
    return match ? match[1] : leaveName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 4)
  }

  const loadLeaveTypes = async () => {
    try {
      const resp = await axios.get<LeaveType[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetLeaveTypes", { withCredentials: true })
      const apiItems: LeaveType[] = Array.isArray(resp.data) ? resp.data : []
      setLeaveTypes(apiItems)
    } catch (e: any) {
      console.error("[LeavePolicies] Failed to fetch leave types:", e)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        console.log("[LeavePolicies] Fetching policies and leave types...")
        const [policiesResp, leaveTypesResp] = await Promise.all([
          axios.get<LeavePolicyRow[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetLeavePolicies", { withCredentials: true }),
          axios.get<LeaveType[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetLeaveTypes", { withCredentials: true }),
        ])

        console.log("[LeavePolicies] Raw policies response:", policiesResp.data)
        console.log("[LeavePolicies] Raw leave types response:", leaveTypesResp.data)

        const policyRows: LeavePolicyRow[] = Array.isArray(policiesResp.data) ? policiesResp.data : []
        const leaveTypesData: LeaveType[] = Array.isArray(leaveTypesResp.data) ? leaveTypesResp.data : []

        // Build a map for leaveTypeId -> leaveName
        const leaveTypeIdToName = new Map<number, string>()
        for (const lt of leaveTypesData) {
          leaveTypeIdToName.set(lt.leaveTypeId, lt.leaveName)
        }

        // Group rows by policyID
        const grouped = new Map<number, Policy>()
        for (const row of policyRows) {
          const existing = grouped.get(row.policyID)
          const leaveTypeName = row.leaveName || leaveTypeIdToName.get(row.leaveTypeID)
          const safeLeaveTypeName = leaveTypeName ?? `Type #${row.leaveTypeID}`

          if (!existing) {
            grouped.set(row.policyID, {
              id: row.policyID,
              name: row.policyName,
              description: row.description,
              status: row.isActive ? "Active" : "Inactive",
              createdBy: row.createdBy,
              leaveTypes: [safeLeaveTypeName],
            })
          } else {
            // Avoid duplicates
            if (!existing.leaveTypes.includes(safeLeaveTypeName)) {
              existing.leaveTypes.push(safeLeaveTypeName)
            }
          }
        }

        const finalPolicies = Array.from(grouped.values())
        console.log("[LeavePolicies] Processed policies:", finalPolicies)
        setPolicies(finalPolicies)
        setLeaveTypes(leaveTypesData)
      } catch (e: any) {
        console.error("[LeavePolicies] Failed to fetch:", e)
        setError(e?.message || "Failed to load leave policies")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleEditLeaveType = (leaveType: LeaveType) => {
    const uiLeaveType = {
      id: leaveType.leaveTypeId,
      name: leaveType.leaveName.replace(/\s*\([^)]*\)\s*$/, "").trim() || leaveType.leaveName,
      code: parseCodeFromName(leaveType.leaveName),
      description: leaveType.description || "",
      colorCode: leaveType.colorCode || "",
      createdBy: leaveType.createdBy || "",
      modifiedBy: leaveType.modifiedBy || "",
      status: leaveType.isActive ? "Active" : "Inactive",
    }
    setEditingLeaveType(uiLeaveType)
    setIsAddModalOpen(true)
  }

  const handleSaveLeaveType = async (data: any) => {
    try {
      console.log("[LeavePolicies] Saving leave type with form data:", data)
      const payload = {
        leaveTypeId: editingLeaveType?.id ?? 0,
        leaveName: data.name,
        description: data.description,
        isActive: data.status === true || data.status === "Active",
        colorCode: data.colorCode,
        modUser: 0,
      }
      console.log("[LeavePolicies] POST payload:", payload)
      await axios.post(process.env.REACT_APP_API_URL +"/api/Leave/SaveLeaveType", payload, { withCredentials: true })
      toast({
        title: "Success",
        description: editingLeaveType ? `Leave type "${data.name}" updated successfully!` : `Leave type "${data.name}" added successfully!`,
        duration: 3000,
        className: "border border-green-300",
      })
      // Reload data
      const fetchData = async () => {
        try {
          const [policiesResp, leaveTypesResp] = await Promise.all([
            axios.get<LeavePolicyRow[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetLeavePolicies", { withCredentials: true }),
            axios.get<LeaveType[]>(process.env.REACT_APP_API_URL +"/api/Leave/GetLeaveTypes", { withCredentials: true }),
          ])

          const policyRows: LeavePolicyRow[] = Array.isArray(policiesResp.data) ? policiesResp.data : []
          const leaveTypesData: LeaveType[] = Array.isArray(leaveTypesResp.data) ? leaveTypesResp.data : []

          const leaveTypeIdToName = new Map<number, string>()
          for (const lt of leaveTypesData) {
            leaveTypeIdToName.set(lt.leaveTypeId, lt.leaveName)
          }

          const grouped = new Map<number, Policy>()
          for (const row of policyRows) {
            const existing = grouped.get(row.policyID)
            const leaveTypeName = row.leaveName || leaveTypeIdToName.get(row.leaveTypeID)
            const safeLeaveTypeName = leaveTypeName ?? `Type #${row.leaveTypeID}`

            if (!existing) {
              grouped.set(row.policyID, {
                id: row.policyID,
                name: row.policyName,
                description: row.description,
                status: row.isActive ? "Active" : "Inactive",
                createdBy: row.createdBy,
                leaveTypes: [safeLeaveTypeName],
              })
            } else {
              if (!existing.leaveTypes.includes(safeLeaveTypeName)) {
                existing.leaveTypes.push(safeLeaveTypeName)
              }
            }
          }

          setPolicies(Array.from(grouped.values()))
          setLeaveTypes(leaveTypesData)
        } catch (e: any) {
          console.error("[LeavePolicies] Reload failed:", e)
        }
      }
      await fetchData()
    } catch (e: any) {
      console.error("[LeavePolicies] Save failed:", e)
      toast({
        title: "Error",
        description: e?.message || "Failed to save leave type",
        duration: 4000,
      })
    } finally {
      setIsAddModalOpen(false)
      setEditingLeaveType(null)
    }
  }

  return (
    <div className="p-6">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-blue-600" />
              Leave Policies
            </CardTitle>
            {/* <Button 
              onClick={() => { 
                setEditingLeaveType(null); 
                setIsAddModalOpen(true); 
              }} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Leave Type
            </Button> */}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loading && (
            <div className="p-4 text-sm text-gray-600">Loading policies...</div>
          )}
          {error && (
            <div className="p-4 text-sm text-red-600">{error}</div>
          )}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {policies.map((policy) => (
                <Card key={policy.id} className="border border-gray-200 rounded-lg shadow-sm bg-white hover:shadow-md transition-shadow">
                  <CardContent className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{policy.name}</h3>
                        <p className="text-sm text-gray-500 mb-2">{policy.name}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-700">{policy.description}</p>
                    
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          policy.status === "Active"
                            ? "border-green-200 text-green-700 bg-green-50"
                            : "border-red-200 text-red-700 bg-red-50"
                        }
                      >
                        {policy.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500">Created By</p>
                      <p className="text-sm text-gray-900 font-semibold">{policy.createdBy}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500">Leave Types</p>
                      <div className="flex flex-wrap gap-2">
                        {policy.leaveTypes.map((type, index) => {
                          const leaveType = leaveTypes.find(lt => lt.leaveName === type || lt.leaveName.includes(type))
                          return (
                            <Badge 
                              key={index} 
                              variant="secondary" 
                              className="bg-blue-100 text-blue-700 flex items-center gap-1"
                            >
                              {leaveType?.colorCode && (
                                <Circle className="h-3 w-3" style={{ color: leaveType.colorCode }} fill={leaveType.colorCode} />
                              )}
                              {type}
                            </Badge>
                          )
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddLeaveTypeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveLeaveType}
        initialData={editingLeaveType}
      />
    </div>
  )
}

